import { useEffect } from 'react'

// Only shortcuts that have real call-site handlers are included here.
// Previously the hook also handled N (create), E (edit), Del/Backspace
// (delete), but those had no bound handlers in CalendarPage and only
// called `e.preventDefault()`, which is worse than not advertising them
// at all — the help overlay implied they worked. If we wire real handlers
// for those actions later, add them back here AND in SHORTCUT_LIST.
interface CalendarShortcutActions {
  onToday?: () => void
  onWeekView?: () => void
  onMonthView?: () => void
  onPrev?: () => void
  onNext?: () => void
  onEscape?: () => void
  onShowHelp?: () => void
}

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

export function useCalendarShortcuts(actions: CalendarShortcutActions) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement
      if (INPUT_TAGS.has(target.tagName)) return
      if (target.isContentEditable) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      switch (e.key) {
        case 't':
        case 'T':
          e.preventDefault()
          actions.onToday?.()
          break
        case 'w':
        case 'W':
          e.preventDefault()
          actions.onWeekView?.()
          break
        case 'm':
        case 'M':
          e.preventDefault()
          actions.onMonthView?.()
          break
        case 'ArrowLeft':
          e.preventDefault()
          actions.onPrev?.()
          break
        case 'ArrowRight':
          e.preventDefault()
          actions.onNext?.()
          break
        case 'Escape':
          actions.onEscape?.()
          break
        case '?':
          e.preventDefault()
          actions.onShowHelp?.()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [actions])
}

export const SHORTCUT_LIST = [
  { key: 'T', description: 'Jump to today' },
  { key: 'W', description: 'Switch to week view' },
  { key: 'M', description: 'Switch to month view' },
  { key: '←', description: 'Previous week/month' },
  { key: '→', description: 'Next week/month' },
  { key: 'Esc', description: 'Close panel' },
  { key: '?', description: 'Show shortcuts' },
] as const
